/*
@license
  Impulse by Archetype Themes (https://archetypethemes.co)
  Access unminified JS in assets/theme.js

  Use this event listener to run your own JS outside of this file.
  Documentation - https://archetypethemes.co/blogs/impulse/javascript-events-for-developers

  document.addEventListener('page:loaded', function() {
    // Page has loaded and theme assets are ready
  });
*/

window.theme = window.theme || {};
window.Shopify = window.Shopify || {};

/* ───────────────────────────────────────────────────────────────────────────
   Shared fresh-food classifier for Ajax API (/cart.js) line items
   ---------------------------------------------------------------
   The JS mirror of snippets/cart-is-fresh.liquid. Keep the two in sync.

   Why it is not just `product_type === 'Fresh'`: product_type and handle are
   both TRANSLATABLE product fields in Shopify, so on the /fr storefront they
   come back localised and every fresh line would be misclassified — which
   silently broke checkout-button state, welcome-set gift validation and the
   subscription discount on /fr.

   IMPORTANT LIMITATION vs the Liquid snippet: /cart.js exposes neither metafields
   nor tags, so this classifier cannot use the fresh-only metafields
   (product.custom.fresh_kcal*, variant.custom.fresh_delivery_cycle) that carry
   the Liquid version on pre-existing carts. SKU is no help either — the live
   fresh SKUs contain no "fresh" token.

   That leaves `properties._category` as the one translation-proof signal here. It
   is written on every add by snippets/fresh-product-form-modern.liquid, so carts
   built after that change are fully covered on /fr. Carts created BEFORE it fall
   back to product_type/handle and so can still misclassify on /fr until the
   customer re-adds. Setting a "Fresh" tag or custom.product_category in the admin
   does NOT help this file, only the Liquid one.

   Installed idempotently on window.HB because the two consumers load in an
   order that is not guaranteed: welcome-set.js is script-tagged inside
   snippets/fresh-product-form-modern.liquid (page content) while this file is
   tagged in the layout footer, so welcome-set.js executes first on the fresh
   product page. Whichever file loads first installs the single definition.
   ─────────────────────────────────────────────────────────────────────────── */
window.HB = window.HB || {};
if (!window.HB.isFreshLineItem) {
  window.HB.isFreshLineItem = function (item) {
    if (!item) return false;

    var props = item.properties || {};

    // 1. Line-item property, written at add-to-cart time. Never translated.
    //    The only translation-proof signal available to JS.
    var propCategory = String(props._category || props.category || '').trim().toLowerCase();
    if (propCategory === 'fresh') return true;

    // 2. LEGACY translatable fallbacks — correct on the primary locale only.
    if (String(item.product_type || '').trim().toLowerCase() === 'fresh') return true;
    if (String(item.handle || '').toLowerCase().indexOf('frischfutter') !== -1) return true;

    return false;
  };
}

/* ───────────────────────────────────────────────────────────────────────────
   window.HB.ensureFreshFirst() — makes the fresh line the FIRST cart line
   ----------------------------------------------------------------------
   Line order matters because the checkout summary shows it. The two-pass render
   in templates/cart.ajax.liquid only fixes the DRAWER, so a cart in the wrong
   order looks fine until checkout — verify against /cart.js, not the UI.

   Shopify PREPENDS a new line on this store (measured: adding to a 2-line cart
   lands the new line at index 0), so assets/welcome-set.js adds the fresh item
   LAST. This is the backstop for every other route into the cart.

   The Ajax API cannot reorder: /cart/change.js and /cart/update.js only change
   quantity, and re-adding an identical line merges instead of moving it. Remove
   + re-add is the only move. A multi-pet order has MORE THAN ONE fresh line (one
   subscription per dog), and the goal is all of them as one contiguous block at
   the front — not just the first. So, per placement direction:

     newest_first  remove ALL fresh lines and re-add them as one batch, which
                   prepends them together at the front
     oldest_first  remove every non-fresh line in front of the last fresh line
                   and re-add them, so they fall in behind the fresh block

   Success is "every fresh line is contiguous from index 0" (freshGroupedAtFront),
   not "a fresh line reached index 0" — the latter was satisfied as soon as one
   dog reached the top while the others stayed below the gift.

   The direction is detected rather than hard-coded (Shopify has flipped it
   before): try the cached one, verify, fall back to the other, cache the winner
   in localStorage.hb_cart_line_placement — which welcome-set.js also reads to
   pick its add order. Never uses /cart/clear, so the cart discount and the
   `applied_discount` guard survive.
   ─────────────────────────────────────────────────────────────────────────── */
if (!window.HB.ensureFreshFirst) {
  (function () {
    // Set only when BOTH directions failed, so buildCart() stops retrying.
    var ABORT_KEY = 'hb_fresh_first_unsupported';
    var PLACEMENT_KEY = 'hb_cart_line_placement';

    function isDisabled() {
      try { return sessionStorage.getItem(ABORT_KEY) === '1'; } catch (e) { return false; }
    }

    function disable() {
      try { sessionStorage.setItem(ABORT_KEY, '1'); } catch (e) { }
    }

    function readPlacement() {
      try { return localStorage.getItem(PLACEMENT_KEY) || ''; } catch (e) { return ''; }
    }

    function writePlacement(value) {
      try { localStorage.setItem(PLACEMENT_KEY, value); } catch (e) { }
    }

    function getCart() {
      return fetch('/cart.js?t=' + Date.now(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function (res) { return res.json(); });
    }

    function post(url, body) {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      }).then(function (res) { return res.json(); });
    }

    // The removal already happened by this point, so a failure here loses the
    // line. Retry once.
    function postAddWithRetry(payload) {
      return post('/cart/add.js', payload).then(function (data) {
        if (!data || data.status !== 422) return data;
        return post('/cart/add.js', payload);
      }).catch(function () {
        return post('/cart/add.js', payload);
      });
    }

    // Indices of every fresh line, in cart order. A multi-pet order has one
    // fresh subscription line per dog, so "fresh first" means ALL of them, not
    // just the first.
    function freshIndices(cart) {
      var items = (cart && cart.items) || [];
      var out = [];
      for (var i = 0; i < items.length; i++) {
        if (window.HB.isFreshLineItem(items[i])) out.push(i);
      }
      return out;
    }

    // True when every fresh line sits in one contiguous block starting at index
    // 0 (0..n-1 with no gift or add-on wedged between them). This replaces the
    // old "first fresh line is at index 0" check, which was satisfied the moment
    // one dog reached the top even while the others stayed below the gift.
    function freshGroupedAtFront(cart) {
      var idx = freshIndices(cart);
      if (!idx.length) return true; // nothing to order
      return idx[0] === 0 && idx[idx.length - 1] === idx.length - 1;
    }

    // /cart.js and /cart/add.js disagree on shape: the selling plan comes back
    // nested but has to be sent flat, and properties must be replayed verbatim
    // or the line loses its _category / welcome-set markers.
    function lineToAddItem(item) {
      var entry = { id: item.variant_id, quantity: item.quantity };

      if (item.properties && Object.keys(item.properties).length) {
        entry.properties = item.properties;
      }

      var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      if (plan && plan.id) entry.selling_plan = plan.id;

      return entry;
    }

    // Returns false when the re-add failed, i.e. the lines are gone.
    function removeThenReAdd(lines) {
      var payload = { items: lines.map(lineToAddItem) };
      var updates = {};
      lines.forEach(function (item) { updates[item.key] = 0; });

      return post('/cart/update.js', { updates: updates })
        .then(function () { return postAddWithRetry(payload); })
        .then(function (data) {
          if (data && data.status === 422) {
            console.error('[fresh-first] could not re-add lines after removing them:',
              data.description || data.message);
            return false;
          }
          return true;
        });
    }

    // buildCart() fires from several overlapping places (hover prefetch,
    // ajaxProduct:added, drawerOpen); share the in-flight run so two reorders
    // cannot remove the same lines twice.
    var inFlight = null;

    // One attempt per direction. Re-reads the cart each time because the
    // previous attempt mutated it.
    function attempt(modes, i) {
      if (i >= modes.length) {
        disable();
        console.warn('[fresh-first] neither direction put the fresh line first; ' +
          'disabled for this session.');
        return Promise.resolve();
      }

      return getCart().then(function (cart) {
        if (freshGroupedAtFront(cart)) return;

        var idx = freshIndices(cart);
        if (!idx.length) {
          console.error('[fresh-first] no fresh line is in the cart; aborting.');
          return;
        }

        // newest_first (store prepends): remove ALL fresh lines and re-add them
        // as one batch, which lands at the front as a contiguous block.
        // oldest_first (store appends): remove every NON-fresh line in front of
        // the last fresh line and re-add them, so they fall in behind the fresh
        // block. Either way the whole fresh group ends up first, not just one.
        var freshSet = {};
        idx.forEach(function (n) { freshSet[n] = true; });
        var lastFresh = idx[idx.length - 1];

        var lines;
        if (modes[i] === 'newest_first') {
          lines = idx.map(function (n) { return cart.items[n]; });
        } else {
          lines = cart.items.slice(0, lastFresh).filter(function (_item, n) {
            return !freshSet[n];
          });
        }

        if (!lines.length) return;

        return removeThenReAdd(lines).then(function (ok) {
          if (!ok) return;
          return getCart().then(function (after) {
            if (freshGroupedAtFront(after)) {
              writePlacement(modes[i]);
              return;
            }
            return attempt(modes, i + 1);
          });
        });
      });
    }

    /**
     * @param {object} [knownCart] a /cart.js payload the caller already has.
     * @returns {Promise<boolean>} true when the cart was mutated, so the caller
     *   knows any cart JSON it holds is stale (line keys changed).
     */
    window.HB.ensureFreshFirst = function (knownCart) {
      if (inFlight) return inFlight;
      if (isDisabled()) return Promise.resolve(false);

      var run = (knownCart ? Promise.resolve(knownCart) : getCart())
        .then(function (cart) {
          // Nothing to do when there is no fresh line, or every fresh line is
          // already one contiguous block at the front.
          if (freshGroupedAtFront(cart)) return false;

          var modes = readPlacement() === 'oldest_first'
            ? ['oldest_first', 'newest_first']
            : ['newest_first', 'oldest_first'];

          return attempt(modes, 0).then(function () { return true; });
        })
        .catch(function (err) {
          console.warn('[fresh-first] reorder failed:', err);
          return false;
        })
        .then(function (changed) {
          inFlight = null;
          return changed;
        });

      inFlight = run;
      return run;
    };
  })();
}

theme.config = {
  bpSmall: false,
  hasSessionStorage: true,
  hasLocalStorage: true,
  mediaQuerySmall: 'screen and (max-width: ' + 769 + 'px)',
  youTubeReady: false,
  vimeoReady: false,
  vimeoLoading: false,
  isTouch: ('ontouchstart' in window) || window.DocumentTouch && window.document instanceof DocumentTouch || window.navigator.maxTouchPoints || window.navigator.msMaxTouchPoints ? true : false,
  stickyHeader: false,
  rtl: document.documentElement.getAttribute('dir') == 'rtl' ? true : false
};

if (theme.config.isTouch) {
  document.documentElement.className += ' supports-touch';
}

window.lazySizesConfig = window.lazySizesConfig || {};
lazySizesConfig.expFactor = 4;

(function () {
  'use strict';

  theme.delegate = {
    on: function (event, callback, options) {
      if (!this.namespaces) // save the namespaces on the DOM element itself
        this.namespaces = {};

      this.namespaces[event] = callback;
      options = options || false;

      this.addEventListener(event.split('.')[0], callback, options);
      return this;
    },
    off: function (event) {
      if (!this.namespaces) { return }
      this.removeEventListener(event.split('.')[0], this.namespaces[event]);
      delete this.namespaces[event];
      return this;
    }
  };

  // Extend the DOM with these above custom methods
  window.on = Element.prototype.on = theme.delegate.on;
  window.off = Element.prototype.off = theme.delegate.off;

  theme.utils = {
    defaultTo: function (value, defaultValue) {
      return (value == null || value !== value) ? defaultValue : value
    },

    wrap: function (el, wrapper) {
      el.parentNode.insertBefore(wrapper, el);
      wrapper.appendChild(el);
    },

    prepareTransition: function (el, callback) {
      el.addEventListener('transitionend', removeClass);

      function removeClass(evt) {
        el.classList.remove('is-transitioning');
        el.removeEventListener('transitionend', removeClass);
      }

      el.classList.add('is-transitioning');

      // Use requestAnimationFrame instead of offsetWidth to avoid forced layout thrashing
      window.requestAnimationFrame(function () {
        if (typeof callback === 'function') {
          callback();
        }
      });
    },

    // _.compact from lodash
    // Creates an array with all falsey values removed. The values `false`, `null`,
    // `0`, `""`, `undefined`, and `NaN` are falsey.
    // _.compact([0, 1, false, 2, '', 3]);
    // => [1, 2, 3]
    compact: function (array) {
      var index = -1,
        length = array == null ? 0 : array.length,
        resIndex = 0,
        result = [];

      while (++index < length) {
        var value = array[index];
        if (value) {
          result[resIndex++] = value;
        }
      }
      return result;
    },

    debounce: function (func, wait, immediate) {
      var timeout;
      return function () {
        var context = this, args = arguments;
        var later = function () {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    },

    serialize: function (form) {
      var arr = [];
      Array.prototype.slice.call(form.elements).forEach(function (field) {
        if (
          !field.name ||
          field.disabled ||
          ['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1
        )
          return;
        if (field.type === 'select-multiple') {
          Array.prototype.slice.call(field.options).forEach(function (option) {
            if (!option.selected) return;
            arr.push(
              encodeURIComponent(field.name) +
              '=' +
              encodeURIComponent(option.value)
            );
          });
          return;
        }
        if (['checkbox', 'radio'].indexOf(field.type) > -1 && !field.checked)
          return;
        arr.push(
          encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value)
        );
      });
      return arr.join('&');
    }
  };

  theme.a11y = {
    trapFocus: function (options) {
      var eventsName = {
        focusin: options.namespace ? 'focusin.' + options.namespace : 'focusin',
        focusout: options.namespace
          ? 'focusout.' + options.namespace
          : 'focusout',
        keydown: options.namespace
          ? 'keydown.' + options.namespace
          : 'keydown.handleFocus'
      };

      // Get every possible visible focusable element
      var focusableEls = options.container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex^="-"])');
      var elArray = [].slice.call(focusableEls);
      var focusableElements = elArray.filter(el => el.offsetParent !== null);

      var firstFocusable = focusableElements[0];
      var lastFocusable = focusableElements[focusableElements.length - 1];

      if (!options.elementToFocus) {
        options.elementToFocus = options.container;
      }

      options.container.setAttribute('tabindex', '-1');
      options.elementToFocus.focus();

      document.documentElement.off('focusin');
      document.documentElement.on(eventsName.focusout, function () {
        document.documentElement.off(eventsName.keydown);
      });

      document.documentElement.on(eventsName.focusin, function (evt) {
        if (evt.target !== lastFocusable && evt.target !== firstFocusable) return;

        document.documentElement.on(eventsName.keydown, function (evt) {
          _manageFocus(evt);
        });
      });

      function _manageFocus(evt) {
        if (evt.keyCode !== 9) return;
        /**
         * On the first focusable element and tab backward,
         * focus the last element
         */
        if (evt.target === firstFocusable && evt.shiftKey) {
          evt.preventDefault();
          lastFocusable.focus();
        }
      }
    },
    removeTrapFocus: function (options) {
      var eventName = options.namespace
        ? 'focusin.' + options.namespace
        : 'focusin';

      if (options.container) {
        options.container.removeAttribute('tabindex');
      }

      document.documentElement.off(eventName);
    },

    lockMobileScrolling: function (namespace, element) {
      var el = element ? element : document.documentElement;
      document.documentElement.classList.add('lock-scroll');
      el.on('touchmove' + namespace, function () {
        return true;
      });
    },

    unlockMobileScrolling: function (namespace, element) {
      document.documentElement.classList.remove('lock-scroll');
      var el = element ? element : document.documentElement;
      el.off('touchmove' + namespace);
    }
  };

  // Add class when tab key starts being used to show outlines
  document.documentElement.on('keyup.tab', function (evt) {
    if (evt.keyCode === 9) {
      document.documentElement.classList.add('tab-outline');
      document.documentElement.off('keyup.tab');
    }
  });

  /**
   * Currency Helpers
   * -----------------------------------------------------------------------------
   * A collection of useful functions that help with currency formatting
   *
   * Current contents
   * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
   *   - When theme.settings.superScriptPrice is enabled, format cents in <sup> tag
   * - getBaseUnit - Splits unit price apart to get value + unit
   *
   */

  theme.Currency = (function () {
    var moneyFormat = '${{amount}}';
    var superScript = theme && theme.settings && theme.settings.superScriptPrice;

    function formatMoney(cents, format) {
      if (!format) {
        format = theme.settings.moneyFormat;
      }

      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      var value = '';
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var formatString = (format || moneyFormat);

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = theme.utils.defaultTo(precision, 2);
        thousands = theme.utils.defaultTo(thousands, ',');
        decimal = theme.utils.defaultTo(decimal, '.');

        if (isNaN(number) || number == null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        var parts = number.split('.');
        var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        var centsAmount = parts[1] ? (decimal + parts[1]) : '';

        return dollarsAmount + centsAmount;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);

          if (superScript && value && value.includes('.')) {
            value = value.replace('.', '<sup>') + '</sup>';
          }

          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');

          if (superScript && value && value.includes('.')) {
            value = value.replace(',', '<sup>') + '</sup>';
          }

          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }

    function getBaseUnit(variant) {
      if (!variant) {
        return;
      }

      if (!variant.unit_price_measurement || !variant.unit_price_measurement.reference_value) {
        return;
      }

      return variant.unit_price_measurement.reference_value === 1
        ? variant.unit_price_measurement.reference_unit
        : variant.unit_price_measurement.reference_value +
        variant.unit_price_measurement.reference_unit;
    }

    return {
      formatMoney: formatMoney,
      getBaseUnit: getBaseUnit
    }
  })();

  // Init section function when it's visible, then disable observer
  theme.initWhenVisible = function (options) {
    var threshold = options.threshold ? options.threshold : 0;

    var observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (typeof options.callback === 'function') {
            options.callback();
            observer.unobserve(entry.target);
          }
        }
      });
    }, { rootMargin: '0px 0px ' + threshold + 'px 0px' });

    observer.observe(options.element);
  };

  theme.Sections = function Sections() {
    this.constructors = {};
    this.instances = [];

    document.addEventListener('shopify:section:load', this._onSectionLoad.bind(this));
    document.addEventListener('shopify:section:unload', this._onSectionUnload.bind(this));
    document.addEventListener('shopify:section:select', this._onSelect.bind(this));
    document.addEventListener('shopify:section:deselect', this._onDeselect.bind(this));
    document.addEventListener('shopify:block:select', this._onBlockSelect.bind(this));
    document.addEventListener('shopify:block:deselect', this._onBlockDeselect.bind(this));
  };

  theme.Sections.prototype = Object.assign({}, theme.Sections.prototype, {
    _createInstance: function (container, constructor, scope) {
      var id = container.getAttribute('data-section-id');
      var type = container.getAttribute('data-section-type');

      constructor = constructor || this.constructors[type];

      if (typeof constructor === 'undefined') {
        return;
      }

      // If custom scope passed, check to see if instance
      // is already initialized so we don't double up
      if (scope) {
        var instanceExists = this._findInstance(id);
        if (instanceExists) {
          this._removeInstance(id);
        }
      }

      // If a section fails to init, handle the error without letting all subsequest section registers to fail
      try {
        var instance = Object.assign(new constructor(container), {
          id: id,
          type: type,
          container: container
        });
        this.instances.push(instance);
      } catch (e) {
        console.error(e);
      }
    },

    _findInstance: function (id) {
      for (var i = 0; i < this.instances.length; i++) {
        if (this.instances[i].id === id) {
          return this.instances[i];
        }
      }
    },

    _removeInstance: function (id) {
      var i = this.instances.length;
      var instance;

      while (i--) {
        if (this.instances[i].id === id) {
          instance = this.instances[i];
          this.instances.splice(i, 1);
          break;
        }
      }

      return instance;
    },

    _onSectionLoad: function (evt, subSection, subSectionId) {
      if (window.AOS) { AOS.refresh() }

      var container = subSection ? subSection : evt.target;
      var section = subSection ? subSection : evt.target.querySelector('[data-section-id]');

      if (!section) {
        return;
      }

      this._createInstance(section);

      var instance = subSection ? subSectionId : this._findInstance(evt.detail.sectionId);

      // Check if we have subsections to load
      var haveSubSections = container.querySelectorAll('[data-subsection]');
      if (haveSubSections.length) {
        this.loadSubSections(container);
      }

      // Run JS only in case of the section being selected in the editor
      // before merchant clicks "Add"
      if (instance && typeof instance.onLoad === 'function') {
        instance.onLoad(evt);
      }

      // Force editor to trigger scroll event when loading a section
      setTimeout(function () {
        window.dispatchEvent(new Event('scroll'));
      }, 200);
    },

    _onSectionUnload: function (evt) {
      this.instances = this.instances.filter(function (instance) {
        var isEventInstance = instance.id === evt.detail.sectionId;

        if (isEventInstance) {
          if (typeof instance.onUnload === 'function') {
            instance.onUnload(evt);
          }
        }

        return !isEventInstance;
      });
    },

    loadSubSections: function (scope) {
      if (!scope) {
        return;
      }

      var sections = scope.querySelectorAll('[data-section-id]');

      sections.forEach(el => {
        this._onSectionLoad(null, el, el.dataset.sectionId);
      });
    },

    _onSelect: function (evt) {
      var instance = this._findInstance(evt.detail.sectionId);

      if (
        typeof instance !== 'undefined' &&
        typeof instance.onSelect === 'function'
      ) {
        instance.onSelect(evt);
      }
    },

    _onDeselect: function (evt) {
      var instance = this._findInstance(evt.detail.sectionId);

      if (
        typeof instance !== 'undefined' &&
        typeof instance.onDeselect === 'function'
      ) {
        instance.onDeselect(evt);
      }
    },

    _onBlockSelect: function (evt) {
      var instance = this._findInstance(evt.detail.sectionId);

      if (
        typeof instance !== 'undefined' &&
        typeof instance.onBlockSelect === 'function'
      ) {
        instance.onBlockSelect(evt);
      }
    },

    _onBlockDeselect: function (evt) {
      var instance = this._findInstance(evt.detail.sectionId);

      if (
        typeof instance !== 'undefined' &&
        typeof instance.onBlockDeselect === 'function'
      ) {
        instance.onBlockDeselect(evt);
      }
    },

    register: function (type, constructor, scope) {
      this.constructors[type] = constructor;


      var sections = document.querySelectorAll('[data-section-type="' + type + '"]');

      if (scope) {
        sections = scope.querySelectorAll('[data-section-type="' + type + '"]');
      }

      sections.forEach(
        function (container) {
          this._createInstance(container, constructor, scope);
        }.bind(this)
      );
    },

    reinit: function (section) {
      for (var i = 0; i < this.instances.length; i++) {
        var instance = this.instances[i];
        if (instance['type'] === section) {
          if (typeof instance.forceReload === 'function') {
            instance.forceReload();
          }
        }
      }
    }
  });


  /*
    Options:
      container
      enableHistoryState - enable when on single product page to update URL
      singleOptionSelector - selector for individual variant option (e.g. 'Blue' or 'Small')
      originalSelectorId - selector for base variant selector (visually hidden)
      variants - JSON parsed object of product variant info
   */
  theme.Variants = (function () {

    function Variants(options) {
      this.container = options.container;
      this.variants = options.variants;
      this.singleOptionSelector = options.singleOptionSelector;

      this.dropdown = options.dropdown,
        this.variantDropdown = options.variantDropdown,
        this.variantDropdownItems = options.variantDropdownItems,

        this.originalSelectorId = options.originalSelectorId;
      this.sellingPlan = options.sellingPlan;
      this.enableHistoryState = options.enableHistoryState;
      this.dynamicVariantsEnabled = options.dynamicVariantsEnabled;
      this.currentlySelectedValues = this._getCurrentOptions();
      this.currentVariant = this._getVariantFromOptions();

      this.container.querySelectorAll(this.singleOptionSelector).forEach(el => {
        el.addEventListener('change', this._onSelectChange.bind(this));
      });

      this.container.querySelectorAll(this.variantDropdownItems).forEach(el => {
        el.addEventListener('click', this._onVariantDropdownChange.bind(this, el));
      })
    }

    Variants.prototype = Object.assign({}, Variants.prototype, {

      _getCurrentOptions: function () {
        var result = [];

        this.container.querySelectorAll(this.singleOptionSelector).forEach(el => {
          var type = el.getAttribute('type');

          if (type === 'radio' || type === 'checkbox') {
            if (el.checked) {
              result.push({
                value: el.value,
                index: el.dataset.index
              });
            }
          } else {
            result.push({
              value: el.value,
              index: el.dataset.index
            });
          }
        });

        // remove any unchecked input values if using radio buttons or checkboxes
        result = theme.utils.compact(result);

        return result;
      },

      // Pull the number out of the option index name, e.g. 'option1' -> 1
      _numberFromOptionKey: function (key) {
        return parseInt(key.substr(-1));
      },

      // Options should be ordered from highest to lowest priority. Make sure that priority
      // is represented using weighted values when finding best match
      _getWeightedOptionMatchCount: function (variant) {
        return this._getCurrentOptions().reduce((count, { value, index }) => {
          const optionIndex = this._numberFromOptionKey(index);
          const weightedCount = 4 - optionIndex; // The lower the index, the better the match we have
          return variant[index] === value ? count + weightedCount : count;
        }, 0)
      },

      _getFullMatch(needsToBeAvailable) {
        const currentlySelectedOptions = this._getCurrentOptions();
        const variants = this.variants;

        return variants.find(variant => {
          const isMatch = currentlySelectedOptions.every(({ value, index }) => {
            return variant[index] === value;
          });

          if (needsToBeAvailable) {
            return isMatch && variant.available;
          } else {
            return isMatch;
          }
        });
      },

      // Find a variant that is available and best matches last selected option
      _getClosestAvailableMatch: function (lastSelectedOption) {
        if (!lastSelectedOption) return null;

        const currentlySelectedOptions = this._getCurrentOptions();
        const variants = this.variants;

        const potentialAvailableMatches = lastSelectedOption && variants.filter(variant => {
          return currentlySelectedOptions
            .filter(
              // Only match based selected options that are equal and preceeding the last selected option
              ({ value, index }) => this._numberFromOptionKey(index) <= this._numberFromOptionKey(lastSelectedOption.index)
            ).every(({ value, index }) => {
              // Variant needs to have options that match the current and preceeding selection options
              return variant[index] === value;
            }) && variant.available
        });

        return potentialAvailableMatches.reduce((bestMatch, variant) => {
          // If this is the first potential match we've found, store it as the best match
          if (bestMatch === null) return variant;

          // If this is not the first potential match, compare the number of options our current best match has in common
          // compared to the next contender.
          const bestMatchCount = this._getWeightedOptionMatchCount(bestMatch, lastSelectedOption);
          const newCount = this._getWeightedOptionMatchCount(variant, lastSelectedOption);

          return newCount > bestMatchCount ? variant : bestMatch;
        }, null);
      },

      _getVariantFromOptions: function (lastSelectedOption) {
        const availableFullMatch = this._getFullMatch(true);
        const closestAvailableMatch = this._getClosestAvailableMatch(lastSelectedOption);
        const fullMatch = this._getFullMatch(false);

        if (this.dynamicVariantsEnabled) {
          // Add some additional smarts to variant matching if Dynamic Variants are enabled
          return availableFullMatch || closestAvailableMatch || fullMatch || null;
        } else {
          // Only return a full match or null (variant doesn't exist) if Dynamic Variants are disabled
          return fullMatch || null;
        }

      },

      _updateInputState: function (variant, el) {
        return (input) => {
          if (variant === null) return;

          const index = input.dataset.index;
          const value = input.value;
          const type = input.getAttribute('type');

          if (type === 'radio' || type === 'checkbox') {
            input.checked = variant[index] === value
          } else {
            input.value = variant[index];
          }
        }
      },

      _onSelectChange: function ({ srcElement }) {
        const optionSelectElements = this.container.querySelectorAll(this.singleOptionSelector);

        // Get the best variant based on the current selection + last selected element
        const variant = this._getVariantFromOptions({
          index: srcElement.dataset.index,
          value: srcElement.value
        });

        // Update DOM option input states based on the variant that was found
        optionSelectElements.forEach(this._updateInputState(variant, srcElement))

        // Make sure our currently selected values are up to date after updating state of DOM
        const currentlySelectedValues = this.currentlySelectedValues = this._getCurrentOptions();

        const detail = {
          variant,
          currentlySelectedValues,
          value: srcElement.value,
          index: srcElement.parentElement.dataset.index
        }

        this.container.dispatchEvent(new CustomEvent('variantChange', { detail }));
        document.dispatchEvent(new CustomEvent('variant:change', { detail }));

        if (!variant) {
          return;
        }

        this._updateMasterSelect(variant);
        this._updateImages(variant);
        this._updatePrice(variant);
        this._updateQuantity(variant);
        this._updateUnitPrice(variant);
        this._updateSKU(variant);
        this.currentVariant = variant;

        if (this.enableHistoryState) {
          this._updateHistoryState(variant);
        }
      },

      _updateImages: function (variant) {
        var variantImage = variant.featured_image || {};
        var currentVariantImage = this.currentVariant && this.currentVariant.featured_image || {};

        if (!variant.featured_image || variantImage.src === currentVariantImage.src) {
          return;
        }

        this.container.dispatchEvent(new CustomEvent('variantImageChange', {
          detail: {
            variant: variant
          }
        }));
      },

      _updatePrice: function (variant) {
        if (this.currentVariant && variant.price === this.currentVariant.price && variant.compare_at_price === this.currentVariant.compare_at_price) {
          return;
        }

        this.container.dispatchEvent(new CustomEvent('variantPriceChange', {
          detail: {
            variant: variant.id
          }
        }));
      },

      _updateQuantity: function (variant) {
        if (this.currentVariant && variant.price === this.currentVariant.price && variant.compare_at_price === this.currentVariant.compare_at_price) {
          return;
        }

        this.container.dispatchEvent(new CustomEvent('variantQuantityChange', {
          detail: {
            variant: variant
          }
        }));
      },

      _updateUnitPrice: function (variant) {
        if (this.currentVariant && variant.unit_price === this.currentVariant.unit_price) {
          return;
        }

        this.container.dispatchEvent(new CustomEvent('variantUnitPriceChange', {
          detail: {
            variant: variant
          }
        }));
      },

      _updateSKU: function (variant) {
        if (this.currentVariant && variant.sku === this.currentVariant.sku) {
          return;
        }

        this.container.dispatchEvent(new CustomEvent('variantSKUChange', {
          detail: {
            variant: variant
          }
        }));
      },

      _updateHistoryState: function (variant) {
        if (!history.replaceState || !variant) {
          return;
        }

        var newurl = window.location.protocol + '//' + window.location.host + window.location.pathname + '?variant=' + variant.id;
        window.history.replaceState({ path: newurl }, '', newurl);
      },

      _updateMasterSelect: function (variant) {

        let masterSelect = this.container.querySelector(this.originalSelectorId);
        if (!masterSelect) return;

        masterSelect.value = variant.id;
        masterSelect.dispatchEvent(new Event('change', { bubbles: true }));
      },

      _onVariantDropdownChange: function (variant) {
        let sellingPlan = this.container.querySelector(this.sellingPlan);
        sellingPlan.value = variant.dataset.sellingPlan;

        let selectedDropdownItem = this.container.querySelector(".dropdown-menu .dropdown-item[data-variant-id='" + variant.dataset.variantId + "']");

        let selectedText = selectedDropdownItem.textContent;

        let dropdownButton = this.container.querySelector(".box-dropdown-btn");
        dropdownButton.textContent = selectedText;

        const selectedVariant = this.variants.find(function (variantTemp) {
          return variantTemp.id == variant.dataset.variantId;
        })


        this._updateMasterSelect(selectedVariant);
        this._updateImages(selectedVariant);
        this._updatePrice(selectedVariant);
        this._updateQuantity(selectedVariant);
        this._updateUnitPrice(selectedVariant);
        this._updateSKU(selectedVariant);
        this.currentVariant = selectedVariant;

        if (this.enableHistoryState) {
          this._updateHistoryState(selectedVariant);
        }

        const dropdown = this.container.querySelector(this.dropdown);
        dropdown.classList.remove('show');
      },

    });

    return Variants;
  })();

  theme.Subscription = (function () {
    function Subscription(options) {

      this.container = options.container;
      this.purchaseTypeSelector = options.purchaseTypeSelector;
      this.originalSelectorId = options.originalSelectorId;
      this.subscription = options.subscription;

      this.settings = {
        activeTabClass: 'product-subscription-tab-active',
        activeTabPaneClass: 'product-subscription-tab-pane-active'
      };

      this.selectors = {
        productSubscription: '.product-subscription',
        productSubscriptionTabs: '.product-subscription-tabs',
        productSubscriptionTabPanes: '.product-subscription-tab-pane',
        productSubscriptionTabActive: '.' + this.settings.activeTabClass,
        productSubscriptionTab: '.product-subscription-tab',
        productSubscriptionRadio: '.product-subscription-radio',

        variant: '[data-variant-input]',

        oldPrice: '[data-product-old-price]',

        sellingPlan: '[name="selling_plan"]'
      };

      this.productSubscription = this.container.querySelector(this.selectors.productSubscription);

      this.container.querySelectorAll(this.purchaseTypeSelector).forEach(el => {
        el.addEventListener('change', this._onSelectChange.bind(this));
      });

      this.init();
    }

    Subscription.prototype = Object.assign({}, Subscription.prototype, {
      init: function () {
        this._setActiveTab();
      },


      _onSelectChange: function ({ srcElement }) {
        const variant = this.container.querySelector(this.originalSelectorId);
        this._updatePrice(variant);
      },

      _updatePrice: function (variant) {
        const variantId = parseInt(variant.value);

        this.container.dispatchEvent(new CustomEvent('subscriptionPriceChange', {
          detail: {
            variant: variantId
          }
        }));
      },

      _setActiveTab: function () {
        const self = this;
        const inputs = this.container.querySelectorAll(this.selectors.productSubscriptionRadio);

        const oldPrice = this.container.querySelector(this.selectors.oldPrice);

        Array.prototype.forEach.call(inputs, function (input) {
          input.addEventListener('change', function (e) {

            self._clearActiveTabs();
            if (e.target.checked) {
              const variant = document.querySelector(self.selectors.variant + ':checked');

              oldPrice.classList.toggle('d-none', e.target.value === "onetime" && parseInt(variant.value) === 1)

              const sellingPlan = self.container.querySelector(self.selectors.sellingPlan);

              sellingPlan.disabled = e.target.value === "onetime";

              const tab = input.closest(self.selectors.productSubscriptionTab);
              tab.classList.add(self.settings.activeTabClass);
              const pane = self.container.querySelector('#' + tab.dataset.id);
              pane.classList.add(self.settings.activeTabPaneClass);
            }
          })
        })
      },

      _clearActiveTabs: function () {
        const self = this;
        const tabs = this.container.querySelectorAll(this.selectors.productSubscriptionTab);

        Array.prototype.forEach.call(tabs, function (tab) {
          tab.classList.remove(self.settings.activeTabClass)
        })

        const panes = this.container.querySelectorAll(this.selectors.productSubscriptionTabPanes);

        Array.prototype.forEach.call(panes, function (pane) {
          pane.classList.remove(self.settings.activeTabPaneClass)
        })
      },
    });

    return Subscription;
  })();

  /**
   * Ajax Renderer
   * -----------------------------------------------------------------------------
   * Render sections without reloading the page.
   * @param {Object[]} sections - The section to update on render.
   * @param {string} sections[].sectionId - The ID of the section from Shopify.
   * @param {string} sections[].nodeId - The ID of the DOM node to replace.
   * @param {Function} sections[].onReplace (optional) - The custom render function.
   * @param {boolean} debug - Output logs to console for debugging.
   *
   */

  theme.AjaxRenderer = (function () {
    function AjaxRenderer({ sections, onReplace, debug } = {}) {
      this.sections = sections || [];
      this.cachedSections = [];
      this.onReplace = onReplace;
      this.debug = Boolean(debug);
    }

    AjaxRenderer.prototype = Object.assign({}, AjaxRenderer.prototype, {
      renderPage: function (basePath, newParams, updateURLHash = true) {
        const currentParams = new URLSearchParams(window.location.search);
        const updatedParams = this.getUpdatedParams(currentParams, newParams)

        const sectionRenders = this.sections.map(section => {

          const url = `${basePath}?section_id=${section.sectionId}&${updatedParams.toString()}`;
          const cachedSectionUrl = cachedSection => cachedSection.url === url;

          return this.cachedSections.some(cachedSectionUrl)
            ? this.renderSectionFromCache(cachedSectionUrl, section)
            : this.renderSectionFromFetch(url, section);
        });

        if (updateURLHash) this.updateURLHash(updatedParams);

        return Promise.all(sectionRenders);
      },

      renderSectionFromCache: function (url, section) {
        const cachedSection = this.cachedSections.find(url);

        this.log(`[AjaxRenderer] rendering from cache: url=${cachedSection.url}`);
        this.renderSection(cachedSection.html, section);
        return Promise.resolve(section);
      },

      renderSectionFromFetch: function (url, section) {
        this.log(`[AjaxRenderer] redering from fetch: url=${url}`);

        return new Promise((resolve, reject) => {
          fetch(url)
            .then(response => response.text())
            .then(responseText => {
              const html = responseText;
              this.cachedSections = [...this.cachedSections, { html, url }];
              this.renderSection(html, section);
              resolve(section);
            })
            .catch(err => reject(err));
        });
      },

      renderSection: function (html, section) {
        this.log(
          `[AjaxRenderer] rendering section: section=${JSON.stringify(section)}`,
        );

        const newDom = new DOMParser().parseFromString(html, 'text/html');
        if (this.onReplace) {
          this.onReplace(newDom, section);
        } else {
          if (typeof section.nodeId === 'string') {
            var newContentEl = newDom.getElementById(section.nodeId);
            if (!newContentEl) {
              return;
            }

            document.getElementById(section.nodeId).innerHTML =
              newContentEl.innerHTML;
          } else {
            section.nodeId.forEach(id => {
              document.getElementById(id).innerHTML =
                newDom.getElementById(id).innerHTML;
            });
          }
        }

        return section;
      },

      getUpdatedParams: function (currentParams, newParams) {
        const clone = new URLSearchParams(currentParams);
        const preservedParams = ['sort_by', 'q', 'options[prefix]', 'type'];

        // Find what params need to be removed
        // delete happens first as we cannot specify keys based off of values
        for (const [key, value] of clone.entries()) {
          if (!newParams.getAll(key).includes(value) && !preservedParams.includes(key)) {
            clone.delete(key);
          };
        }

        // Find what params need to be added
        for (const [key, value] of newParams.entries()) {
          if (!clone.getAll(key).includes(value) && value !== '') {
            clone.append(key, value);
          }
        }

        return clone;
      },

      updateURLHash: function (searchParams) {
        history.pushState(
          {},
          '',
          `${window.location.pathname}${searchParams && '?'.concat(searchParams)
          }`,
        );
      },

      log: function (...args) {
        if (this.debug) {
          console.log(...args);
        }
      },
    });

    return AjaxRenderer;
  })();

  if (window.Shopify && window.Shopify.theme && navigator && navigator.sendBeacon && window.Shopify.designMode) {
    navigator.sendBeacon('https://api.archetypethemes.co/api/beacon', new URLSearchParams({
      shop: window.Shopify.shop,
      themeName: window.theme && window.theme.settings && `${window.theme.settings.themeName} v${window.theme.settings.themeVersion}`,
      role: window.Shopify.theme.role,
      route: window.location.pathname,
      themeId: window.Shopify.theme.id,
      themeStoreId: window.Shopify.theme.theme_store_id || 0,
      isThemeEditor: !!window.Shopify.designMode
    }))
  }
  theme.cart = {
    getCart: function () {
      var url = ''.concat(theme.routes.cart, '?t=').concat(Date.now());
      return fetch(url, {
        credentials: 'same-origin',
        method: 'GET'
      }).then(response => response.json());
    },

    getCartProductMarkup: function () {
      /*
       * The drawer renders its items by fetching this HTML and injecting it, so the
       * request MUST carry the locale prefix or Shopify renders the template in the
       * default language.
       *
       * It used to use `theme.routes.cartPage` (`{{ routes.cart_url }}`), which on
       * this storefront renders WITHOUT the prefix — confirmed in DevTools: a page at
       * /fr/products/... requested "/cart?t=...&view=ajax". Shopify returned German
       * items, which were injected into an otherwise French drawer. Requesting
       * "/fr/cart?view=ajax" by hand returns correct French, so the prefix is the
       * entire fix.
       *
       * That is also why only the drawer CONTENTS were German while the shell
       * ("Panier", "Paiement sécurisé") was French: the shell is part of the French
       * page render, the items are this separate request.
       *
       * The prefix is taken from the CURRENT URL rather than from Liquid's routes or
       * locale objects, because those proved unreliable here: `routes.cart_url` and
       * `Shopify.routes.root` are both unprefixed on this storefront. The first path
       * segment is used only when it matches one of the shop's published locales
       * (theme.publishedLocales, emitted by the layout), so a collection or product
       * path can never be mistaken for a locale. On the primary language there is no
       * segment to match, so this yields "/" and German is unchanged.
       */
      var root = '/';
      try {
        var locales = (typeof theme !== 'undefined' && theme.publishedLocales) || [];
        var segment = (window.location.pathname.split('/')[1] || '').toLowerCase();
        for (var i = 0; i < locales.length; i++) {
          if (String(locales[i]).toLowerCase() === segment) {
            root = '/' + segment + '/';
            break;
          }
        }
      } catch (e) {
        root = '/';
      }

      var url = root + 'cart?t=' + Date.now() + '&view=ajax';

      return fetch(url, {
        credentials: 'same-origin',
        method: 'GET'
      })
        .then(function (response) { return response.text() });
    },

    changeItem: function (key, qty) {
      return this._updateCart({
        url: ''.concat(theme.routes.cartChange, '?t=').concat(Date.now()),
        data: JSON.stringify({
          id: key,
          quantity: qty
        })
      })
    },

    _updateCart: function (params) {
      return fetch(params.url, {
        method: 'POST',
        body: params.data,
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(response => response.json())
        .then(function (cart) {
          return cart;
        });
    },

    updateAttribute: function (key, value) {
      return this._updateCart({
        url: '/cart/update.js',
        data: JSON.stringify({
          attributes: {
            [key]: theme.cart.attributeToString(value)
          }
        })
      });
    },

    updateNote: function (note) {
      return this._updateCart({
        url: '/cart/update.js',
        data: JSON.stringify({
          note: theme.cart.attributeToString(note)
        })
      });
    },

    attributeToString: function (attribute) {
      if ((typeof attribute) !== 'string') {
        attribute += '';
        if (attribute === 'undefined') {
          attribute = '';
        }
      }
      return attribute.trim();
    },

    getDiscountFromCookie: function () {
      var params = new URLSearchParams(window.location.search);
      var urlCode = params.get('discount');
      if (urlCode && urlCode.trim()) {
        return urlCode.trim();
      }
      var getCookie = (window.modernTheme && modernTheme.helpers && modernTheme.helpers.getCookie) || function (name) {
        var value = "; " + document.cookie;
        var parts = value.split("; " + name + "=");
        return parts.length === 2 ? parts.pop().split(";").shift() : undefined;
      };
      return getCookie('discount_code') || '';
    },

    applyDiscountCode: function (discountCode) {
      var code = discountCode == null ? '' : String(discountCode);

      if (sessionStorage.getItem('applied_discount') === code) {
        return Promise.resolve();
      }

      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ discount: code })
      }).then(function () {
        try {
          sessionStorage.setItem('applied_discount', code);
        } catch (e) { }
      }).catch(function () {
        return Promise.resolve();
      });
    },

    getPetconApiBase: function () {
      var host = window.location.hostname;
      return (host.indexOf('localhost') !== -1 || host.indexOf('staging') !== -1)
        ? 'https://petcon-api.dev.hellobello.at'
        : 'https://petcon-api.hellobello.at';
    },

    resolveDiscount: function (rawCode) {
      var lookup = (rawCode && rawCode.trim()) ? rawCode.trim() : '__standard__';
      return fetch(this.getPetconApiBase() + '/api/v1/discount/' + encodeURIComponent(lookup))
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var d = (data && data.data && data.data.discount) ? data.data.discount : null;
          if (!d || typeof d.amount === 'undefined' || !d.code) return null;
          return { code: d.code, amount: d.amount };
        })
        .catch(function () { return null; });
    },

    // Both go through window.HB.isFreshLineItem rather than comparing
    // product_type directly: product_type is translatable, so the old
    // `=== 'Fresh'` test was false for every line on /fr, which meant the
    // subscription discount was never applied there.
    cartHasSubscriptionFresh: function (cart) {
      var items = (cart && cart.items) ? cart.items : [];
      return items.some(function (it) {
        return window.HB.isFreshLineItem(it) && it.selling_plan_allocation;
      });
    },

    cartHasFresh: function (cart) {
      var items = (cart && cart.items) ? cart.items : [];
      return items.some(function (it) {
        return window.HB.isFreshLineItem(it);
      });
    },

    applyDiscountFromCookie: function () {
      var self = this;
      var rawCode = this.getDiscountFromCookie();
      self._resolvedDiscount = null;

      return fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          if (self.cartHasSubscriptionFresh(cart)) {
            return self.resolveDiscount(rawCode).then(function (resolved) {
              if (!resolved) return;
              self._resolvedDiscount = resolved;
              return self.applyDiscountCode(resolved.code);
            });
          }
          if (self.cartHasFresh(cart)) {
            return self.applyDiscountCode('');
          }
          return null;
        })
        .catch(function () { });
    },

    updateCartItemDiscounts: function (container) {
      if (!container) return;

      function updateSubtotalFromItems(containerEl) {
        var form = containerEl && containerEl.closest ? containerEl.closest('form') : null;
        var subtotalEl = form ? form.querySelector('[data-subtotal]') : null;
        if (!subtotalEl) return;

        var allItems = containerEl.querySelectorAll('.cart__item');
        var totalCents = 0;

        allItems.forEach(function (item) {
          var qtyInput = item.querySelector('.js-qty__num');
          var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
          var priceCents;
          var finalAttr = item.getAttribute('data-item-final-price');

          if (item.classList.contains('cart__item-fresh')) {
            priceCents = finalAttr ? parseInt(finalAttr, 10) : parseInt(item.getAttribute('data-item-original-price'), 10) || 0;
          } else {
            priceCents = finalAttr ? parseInt(finalAttr, 10) : 0;
          }

          if (!isNaN(priceCents) && priceCents >= 0) {
            totalCents += priceCents * qty;
          }
        });

        subtotalEl.innerHTML = theme.Currency.formatMoney(totalCents, theme.settings.moneyFormat);
        var checkoutPrice = document.querySelector('[data-checkout-price]');
        if (checkoutPrice) {
          checkoutPrice.innerHTML = theme.Currency.formatMoney(totalCents, theme.settings.moneyFormat);
        }
      }

      var form = container.closest ? container.closest('form') : null;

      var defaultPct = parseInt(container.getAttribute('data-default-discount'), 10);
      if (isNaN(defaultPct)) defaultPct = 30;
      var resolved = this._resolvedDiscount;
      var appliedPct = (resolved && typeof resolved.amount === 'number') ? resolved.amount : 0;
      var displayPct = appliedPct > 0 ? appliedPct : defaultPct;

      var freshItems = container.querySelectorAll('.cart__item-fresh[data-item-original-price]');
      Array.prototype.forEach.call(freshItems, function (item) {
        var isSubscription = item.getAttribute('data-is-subscription') === 'true';
        var originalCents = parseInt(item.getAttribute('data-item-original-price'), 10);
        if (isNaN(originalCents)) return;

        var pct = (isSubscription && appliedPct > 0) ? appliedPct : 0;
        var finalCents = Math.round(originalCents * (100 - pct) / 100);
        item.setAttribute('data-item-final-price', String(finalCents));

        var finalEl = item.querySelector('[data-cart-item-final-price]');
        var originalEl = item.querySelector('[data-cart-item-original-price]');

        if (finalEl) {
          finalEl.textContent = theme.Currency.formatMoney(finalCents, theme.settings.moneyFormat);
        }

        if (pct > 0) {
          if (originalEl) {
            originalEl.textContent = theme.Currency.formatMoney(originalCents, theme.settings.moneyFormat);
            originalEl.style.display = '';
          } else if (finalEl) {
            var origSpan = document.createElement('span');
            origSpan.className = 'cart__item-price--original';
            origSpan.setAttribute('data-cart-item-original-price', '');
            origSpan.textContent = theme.Currency.formatMoney(originalCents, theme.settings.moneyFormat);
            finalEl.parentNode.insertBefore(origSpan, finalEl);
          }
        } else if (originalEl) {
          originalEl.style.display = 'none';
        }

        var discountDiv = item.querySelector('.cart__item-discount');
        if (discountDiv) {
          if (pct > 0) {
            var pctEl = discountDiv.querySelector('[discount-code-percentage]');
            if (pctEl) {
              pctEl.textContent = pct;
              pctEl.style.visibility = 'visible';
            }
            discountDiv.style.display = '';
          } else {
            discountDiv.style.display = 'none';
          }
        }
      });

      var benefitSpans = container.querySelectorAll('.drawer__purchase-benefits [discount-code-percentage]');
      Array.prototype.forEach.call(benefitSpans, function (el) {
        el.textContent = displayPct;
        el.style.visibility = 'visible';
      });

      updateSubtotalFromItems(container);
      if (form) form.classList.remove('discount-updating');
    }
  }

  /*============================================================================
    CartForm
    - Prevent checkout when terms checkbox exists
    - Listen to quantity changes, rebuild cart (both widget and page)
  ==============================================================================*/
  theme.CartForm = (function () {
    var selectors = {
      products: '[data-products]',
      qtySelector: '.js-qty__wrapper',
      discounts: '[data-discounts]',
      savings: '[data-savings]',
      subTotal: '[data-subtotal]',

      cartBubble: '.cart-count-bubble',
      cartNote: '[name="note"]',
      termsCheckbox: '.cart__terms-checkbox',
      checkoutBtn: '.cart__checkout',

      loader: '.cart__loader'
    };

    var classes = {
      btnLoading: 'btn--loading'
    };

    var config = {
      requiresTerms: false
    };

    function CartForm(form) {
      if (!form) {
        return;
      }

      this.form = form;
      this.sectionForm = form.closest('.section-form');
      this.wrapper = form.parentNode;
      this.location = form.dataset.location;
      this.namespace = '.cart-' + this.location;
      this.products = form.querySelector(selectors.products)
      this.submitBtn = form.querySelector(selectors.checkoutBtn);

      this.discounts = form.querySelector(selectors.discounts);
      this.savings = form.querySelector(selectors.savings);
      this.subtotal = form.querySelector(selectors.subTotal);
      this.termsCheckbox = form.querySelector(selectors.termsCheckbox);
      this.noteInput = form.querySelector(selectors.cartNote);

      this.loader = form.querySelector(selectors.loader);

      this.debouncedUpdate = theme.utils.debounce(this.updateQuantityRequest.bind(this), 300);

      if (this.termsCheckbox) {
        config.requiresTerms = true;
      }

      this.init();
    }

    CartForm.prototype = Object.assign({}, CartForm.prototype, {
      init: function () {
        this.setDogName();

        // Use event delegation for quantity changes to avoid re-binding on every render
        this.form.addEventListener('click', this.onFormClick.bind(this));
        this.form.addEventListener('change', this.onFormChange.bind(this));

        this.form.on('submit' + this.namespace, this.onSubmit.bind(this));

        if (this.noteInput) {
          this.noteInput.addEventListener('change', function () {
            var newNote = this.value;
            theme.cart.updateNote(newNote);
          });
        }

        // Dev-friendly way to build the cart
        document.addEventListener('cart:build', function () {
          this.buildCart();
        }.bind(this));

        // Cart page: rebuild on load so discount from URL/cookie is applied
        if (this.location === 'page' && this.products && this.products.children.length > 0) {
          if (this.loader) this.loader.classList.add('cart__loader--visible');
          this.buildCart();
        }
      },

      reInit: function () {},

      onFormClick: function (evt) {
        var target = evt.target;
        var btn = target.closest('.js-qty__adjust');
        if (!btn) return;

        evt.preventDefault();
        var wrapper = btn.closest('.js-qty__wrapper');
        var input = wrapper.querySelector('.js-qty__num');
        var qty = parseInt(input.value);
        var key = input.dataset.id;

        if (btn.classList.contains('js-qty__adjust--plus')) {
          qty++;
        } else if (btn.classList.contains('js-qty__adjust--minus')) {
          qty = Math.max(0, qty - 1);
        } else if (btn.classList.contains('js-qty__adjust--0')) {
          qty = 0;
        }

        input.value = qty;

        // Optimistic UI: Hide savings and subtotal if quantity is zero (removing item)
        // This provides instant feedback before the server responds
        if (qty === 0) {
          var itemRow = btn.closest('.cart__item');
          if (itemRow) itemRow.style.opacity = '0.5';

          // If the customer is removing the fresh item AND welcome-set gifts
          // are still in the cart, that leaves the cart in the orphaned-gift
          // state — disable checkout immediately for instant feedback.
          // validateGifts on the next buildCart reaffirms or re-enables.
          // Removing a fresh item when there are NO welcome-set gifts must
          // NOT disable checkout (other products can still be bought).
          var category = itemRow && itemRow.dataset.category;
          if (category === 'fresh') {
            var hasWelcomeGiftInCart = !!document.querySelector(
              '.cart__items .cart__item--welcome-box, .cart__page-col .cart__item--welcome-box'
            );
            if (hasWelcomeGiftInCart) {
              this.toggleCheckoutButton(false);
            }
          }
        }

        this.debouncedUpdate(key, qty, wrapper);
      },

      onFormChange: function (evt) {
        var target = evt.target;
        if (target.classList.contains('js-qty__num')) {
          var qty = parseInt(target.value);
          var key = target.dataset.id;
          var wrapper = target.closest('.js-qty__wrapper');
          this.debouncedUpdate(key, qty, wrapper);
        }
      },

      updateQuantityRequest: function (key, qty, el) {
        if (!key || qty === undefined) return;

        if (el) el.classList.add('is-loading');
        // Use cart--busy for blur+spinner (same as product-page add). Do NOT also
        // show cart__loader since that creates a double-spinner.
        if (this.form) this.form.classList.add('cart--busy');

        var self = this;

        // When removing a Fresh item (qty=0), also remove its welcome-set gifts
        // in the SAME /cart/update.js call so the cart renders the final state in
        // one shot (no orphaned-gift intermediate, no second buildCart cycle).
        var itemRow = el ? el.closest('.cart__item') : null;
        var isFreshRemoval = qty === 0 && itemRow && itemRow.dataset &&
          itemRow.dataset.category === 'fresh';

        if (isFreshRemoval) {
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (res) { return res.json(); })
            .then(function (cart) {
              var updates = {};
              updates[key] = 0;

              // Only remove welcome-set gifts if no OTHER Fresh items will
              // remain after this removal.
              var otherFreshExists = (cart.items || []).some(function (item) {
                if (item.key === key) return false; // the one being removed
                return window.HB.isFreshLineItem(item);
              });

              if (!otherFreshExists) {
                (cart.items || []).forEach(function (item) {
                  var props = item.properties || {};
                  if (props['_subscription_welcome_set'] || props['_allergy_welcome_set']) {
                    updates[item.key] = 0;
                  }
                });
              }
              return fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ updates: updates })
              }).then(function (res) { return res.json(); });
            })
            .then(function (cart) {
              if (cart.item_count > 0) {
                self.wrapper.classList.remove('is-empty');
              } else {
                self.wrapper.classList.add('is-empty');
              }
              self.buildCart(cart);
              document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { cart: cart }
              }));
            })
            .catch(function () {
              if (self.form) self.form.classList.remove('cart--busy');
              if (el) el.classList.remove('is-loading');
            });
        } else {
          theme.cart.changeItem(key, qty)
            .then(function (cart) {
              if (cart.item_count > 0) {
                self.wrapper.classList.remove('is-empty');
              } else {
                self.wrapper.classList.add('is-empty');
              }
              self.buildCart(cart);
              document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { cart: cart }
              }));
            })
            .catch(function () {
              if (self.form) self.form.classList.remove('cart--busy');
              if (el) el.classList.remove('is-loading');
            });
        }
      },

      onSubmit: function (evt) {
        this.submitBtn.classList.add(classes.btnLoading);

        if (config.requiresTerms) {
          if (this.termsCheckbox.checked) {
            // continue to checkout
          } else {
            alert(theme.strings.cartTermsConfirmation);
            this.submitBtn.classList.remove(classes.btnLoading)
            evt.preventDefault();
            return false;
          }
        }
      },

      /*============================================================================
        Query cart page to get markup
      ==============================================================================*/
      _parseProductHTML: function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        return {
          items: doc.querySelector('.cart__items'),
          discounts: doc.querySelector('.cart__discounts')
        }
      },

      buildCart: function (cartData, skipMarkup) {
        var self = this;

        // Reorder BEFORE fetching the markup so the injected HTML is already
        // final. Doing it after cartMarkup() would need a second buildCart() and
        // a second innerHTML wipe, which is visible. Nothing extra to mask: every
        // caller has already set `cart--busy` / `cart__loader--visible`, cleared
        // at the end of cartMarkup().
        var freshFirst = (window.HB && window.HB.ensureFreshFirst)
          ? window.HB.ensureFreshFirst(cartData)
          : Promise.resolve(false);

        freshFirst.then(function (reordered) {
          // A reorder rewrites line keys, so anything the caller handed us is stale.
          if (reordered) cartData = null;
          return theme.cart.applyDiscountFromCookie();
        }).then(function () {
          var promises = [];
          if (!skipMarkup) {
            promises.push(theme.cart.getCartProductMarkup());
          }
          if (!cartData) {
            promises.push(theme.cart.getCart());
          }
          return Promise.all(promises);
        }).then(function (results) {
          var markup = !skipMarkup ? results[0] : null;
          var data = cartData || (skipMarkup ? results[0] : results[1]);

          if (markup) {
            self.cartMarkup(markup);
          }

          if (data) {
            var cart = {
              total_price: data.total_price / 100,
              $value: data.total_price / 100,
              total_discount: data.total_discount,
              original_total_price: data.original_total_price / 100,
              items: data.items
            };

            if (typeof _learnq !== 'undefined') {
              _learnq.push(['track', 'Added to Cart', cart]);
            }

            self.lastCartCount = data.item_count;
            self.updateCount(data.item_count);

            if (!markup && data.total_price !== undefined) {
              self.updateSubtotal(data.total_price);
            }

            self.validateGifts(data);
          }
        });
      },

      cartMarkup: function (html) {
        var markup = this._parseProductHTML(html);
        var items = markup.items;
        var count = parseInt(items.dataset.count);
        var subtotal = items.dataset.cartSubtotal;
        var savings = items.dataset.cartSavings;

        this.updateCartDiscounts(markup.discounts);
        this.updateSavings(savings);

        if (count > 0) {
          this.wrapper.classList.remove('is-empty');
        } else {
          this.wrapper.classList.add('is-empty');
          if (this.sectionForm) this.sectionForm.remove();
        }

        this.updateCount(count);

        // Hide discount-dependent elements before appending (prevents flash of wrong values)
        if (this.form) this.form.classList.add('discount-updating');

        // Append item markup
        this.products.innerHTML = '';
        this.products.append(items);

        // Update subtotal
        this.subtotal.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);

        // Update cart item prices/discounts from Petcon API (same as product page)
        theme.cart.updateCartItemDiscounts(this.products);

        this.reInit();

        if (window.AOS) { AOS.refresh() }

        if (Shopify && Shopify.StorefrontExpressButtons) {
          Shopify.StorefrontExpressButtons.initialize();
        }

        // Trigger cart update event for apps
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

        // remove loader and blur
        if (this.loader) this.loader.classList.remove('cart__loader--visible');
        if (this.form) this.form.classList.remove('cart--busy');

        // Auto-close the drawer when the cart becomes empty
        if (count === 0 && theme.CartDrawer && theme.CartDrawer.instance) {
          setTimeout(function () {
            theme.CartDrawer.instance.close();
          }, 500);
        }
      },

      updateCartDiscounts: function (markup) {
        if (!this.discounts) {
          return;
        }
        this.discounts.innerHTML = '';
        this.discounts.append(markup);
      },

      /*============================================================================
        Update elements of the cart
      ==============================================================================*/
      updateSubtotal: function (subtotal) {
        this.form.querySelector(selectors.subTotal).innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
        var checkoutPrice = document.querySelector('[data-checkout-price]');
        if (checkoutPrice) {
          checkoutPrice.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
        }
      },

      updateSavings: function (savings) {
        if (!this.savings) {
          return;
        }

        if (savings > 0) {
          var amount = theme.Currency.formatMoney(savings, theme.settings.moneyFormat);
          this.savings.classList.remove('hide');
          this.savings.innerHTML = theme.strings.cartSavings.replace('[savings]', amount);
        } else {
          this.savings.classList.add('hide');
        }
      },

      updateCount: function (count) {
        if (!this.countEls) {
          this.countEls = document.querySelectorAll('.cart-count');
        }
        if (!this.bubbles) {
          this.bubbles = document.querySelectorAll(selectors.cartBubble);
        }

        var html = document.documentElement;
        var previousCount = Number(localStorage.getItem('cartItemsCount'));

        if (this.countEls.length) {
          this.countEls.forEach(el => {
            el.innerText = count;
          });
        }

        // show/hide bubble(s)
        if (this.bubbles.length) {
          if (count > 0) {
            this.bubbles.forEach(b => {
              b.classList.add('cart-count-bubble--visible');
            });
          } else {
            this.bubbles.forEach(b => {
              b.classList.remove('cart-count-bubble--visible');
            });
          }
        }

        localStorage.setItem('cartItemsCount', count);

        if (previousCount !== count && count > 0 && !html.classList.contains('js-drawer-open')) {
          setTimeout(function () {
            if (theme.CartDrawer && theme.CartDrawer.instance) {
              theme.CartDrawer.instance.open();
            }
          }, 300);
        }
      },


      setDogName: function () {
        const dogNameInput = document.getElementById('dog-name');
        const button = document.getElementById("box-input-btn");

        if (dogNameInput) {
          dogNameInput.addEventListener('input', function (e) {
            const dogNameOutput = document.getElementById('pet-name');
            dogNameOutput.value = e.target.value;
            button.style.backgroundColor = e.target.value.trim() ? '#00756c' : '#E1563E';

            localStorage.setItem('dogName', e.target.value.trim());
          });
        }
      },

      validateGifts: function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
          // Empty cart → nothing to gate. Leave checkout enabled (Shopify
          // won't allow checking out an empty cart anyway).
          this.toggleCheckoutButton(true);
          return;
        }

        var self = this;
        var hasFreshItem = false;
        var hasSubscriptionFreshItem = false;
        var hasOneTimeFreshItem = false;
        var hasWelcomeSetGift = false;
        var giftsInCart = [];

        // 1. First pass: Identify "Fresh" items and potential "Gifts"
        cart.items.forEach(function (item) {
          var handle = (item.handle || '').toLowerCase();
          var properties = item.properties || {};

          // Identify Fresh Item (The Trigger). Shared classifier, so this agrees
          // with the Liquid render grouping on every locale — inlining a
          // product_type check here is what broke the /fr cart.
          var isFresh = window.HB.isFreshLineItem(item);

          if (isFresh) {
            hasFreshItem = true;
            if (item.selling_plan_allocation) {
              hasSubscriptionFreshItem = true;
            } else {
              hasOneTimeFreshItem = true;
            }
          }

          // Identify Gift Item
          var hasGiftDiscount = false;
          if (item.line_level_discount_allocations) {
            hasGiftDiscount = item.line_level_discount_allocations.some(function (disc) {
              return disc.discount_application &&
                (disc.discount_application.title === 'hb-wb' ||
                  disc.discount_application.key === 'hb-wb');
            });
          }

          // Welcome-set components (added by snippets/fresh-product-form-modern.liquid)
          // are recognised even before the `hb-wb` Shopify automatic discount
          // has allocated to the line, because their handles do not match the
          // legacy bundle handle (`willkommens-set`).
          var isWelcomeSetComponent =
            properties['_subscription_welcome_set'] ||
            properties['_allergy_welcome_set'];

          if (isWelcomeSetComponent) {
            hasWelcomeSetGift = true;
          }

          var isGift = handle.indexOf('willkommens-set') !== -1 ||
            handle.indexOf('uberraschungen') !== -1 ||
            hasGiftDiscount ||
            properties['_is_gift'] === 'true' ||
            properties['igId'] !== undefined ||
            isWelcomeSetComponent;

          if (isGift) {
            giftsInCart.push(item);
          }
        });

        // Toggle the "Zur Kasse" / checkout button.
        //
        // Only disable in the broken/orphaned state: welcome-set gift items
        // are in the cart but their required fresh item has been removed.
        // A cart that has other products (and no welcome-set gifts) stays
        // checkout-able even without a fresh item.
        var orphanedGifts = hasWelcomeSetGift && !hasFreshItem;
        this.toggleCheckoutButton(!orphanedGifts);

        if (giftsInCart.length === 0) return;

        // 2. Second pass: Validate the identified gifts.
        //
        // The welcome-set component branches below intentionally ignore
        // `final_line_price` so that components stay in the cart while the
        // Shopify automatic discount `hb-wb` is still allocating to them. This
        // is the contract that lets the multi-component welcome set work at
        // full price client-side and zero price at checkout.
        var invalidGifts = giftsInCart.filter(function (gift) {
          var price = gift.final_line_price !== undefined ? gift.final_line_price : gift.line_price;
          var giftProps = gift.properties || {};

          // Welcome-set components (subscription or allergy markers) are
          // valid as long as the fresh item of their purchase type is still
          // in the cart. The `_welcome_set_for` property records which
          // purchase type(s) a gift belongs to:
          //   'subscription' → needs a subscription fresh item
          //   'one_time'     → needs a one-time fresh item
          //   'both'         → needs ANY fresh item (kept while either remains)
          // Older gifts added before this property existed fall back to
          // 'both' so they stay valid while any fresh item is present.
          if (giftProps['_subscription_welcome_set'] || giftProps['_allergy_welcome_set']) {
            var forType = giftProps['_welcome_set_for'] || 'both';
            if (forType === 'subscription') {
              return !hasSubscriptionFreshItem;
            }
            if (forType === 'one_time') {
              return !hasOneTimeFreshItem;
            }
            // 'both' (or unknown): valid while any fresh item remains.
            return !hasFreshItem;
          }

          // Regular gifts: invalid if overpriced or no fresh item at all
          var isOverpriced = price > 0;
          var isOrphaned = !hasFreshItem;

          return isOverpriced || isOrphaned;
        });

        // Welcome-set gifts must always be quantity 1, no matter how many (or
        // what quantity of) fresh items are in the cart. A gift line can end
        // up with quantity > 1 if /cart/add.js bumps an existing line or the
        // quantity stepper is used. Normalise any such line back to 1 (unless
        // it is already being removed below).
        var removalKeys = {};
        invalidGifts.forEach(function (g) { removalKeys[g.key] = true; });

        var excessGifts = giftsInCart.filter(function (gift) {
          if (removalKeys[gift.key]) return false;
          var giftProps = gift.properties || {};
          var isWelcomeSet = giftProps['_subscription_welcome_set'] || giftProps['_allergy_welcome_set'];
          return isWelcomeSet && gift.quantity > 1;
        });

        if (invalidGifts.length === 0 && excessGifts.length === 0) return;

        // Apply all corrections in a SINGLE /cart/update.js call instead of
        // sequential /cart/change.js calls. This makes gift removal instant
        // (one round-trip instead of N).
        var updates = {};
        invalidGifts.forEach(function (g) { updates[g.key] = 0; });
        excessGifts.forEach(function (g) { updates[g.key] = 1; });

        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates })
        })
          .then(function (res) { return res.json(); })
          .then(function (lastCart) {
            self.buildCart(lastCart);
          });
      },

      /**
       * Disable the Zur Kasse / checkout button when no Fresh item is in the
       * cart, and re-enable it otherwise. Called from validateGifts on every
       * cart rebuild so cart drawer + standalone cart page stay in sync.
       *
       * Adds a `cart__checkout--disabled` class for any styling hooks; the
       * native `disabled` attribute is what actually blocks the click.
       */
      toggleCheckoutButton: function (enable) {
        var buttons = document.querySelectorAll('.cart__checkout');
        buttons.forEach(function (btn) {
          if (enable) {
            btn.disabled = false;
            btn.removeAttribute('aria-disabled');
            btn.classList.remove('cart__checkout--disabled');
          } else {
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
            btn.classList.add('cart__checkout--disabled');
          }
        });
      },
    });

    return CartForm;
  })();

  theme.Drawers = (function () {
    function Drawers(id, name) {
      this.config = {
        id: id,
        close: '.js-drawer-close',
        open: '.js-drawer-open-' + name,
        openClass: 'js-drawer-open',
        closingClass: 'js-drawer-closing',
        activeDrawer: 'drawer--is-open',
        namespace: '.drawer-' + name
      };

      this.nodes = {
        page: document.querySelector('#MainContent')
      };

      this.drawer = document.querySelector('#' + id);
      this.isOpen = false;

      if (!this.drawer) {
        return;
      }

      this.init();
    }

    Drawers.prototype = Object.assign({}, Drawers.prototype, {
      init: function () {
        // Setup open button(s)
        document.querySelectorAll(this.config.open).forEach(openBtn => {
          openBtn.setAttribute('aria-expanded', 'false');
          openBtn.addEventListener('click', this.open.bind(this));
        });

        this.drawer.querySelector(this.config.close).addEventListener('click', this.close.bind(this));

        // Close modal if a drawer is opened
        document.addEventListener('modalOpen', function () {
          this.close();
        }.bind(this));
      },

      open: function (evt, returnFocusEl) {
        if (evt) {
          evt.preventDefault();
        }

        if (this.isOpen) {
          return;
        }

        // Without this the drawer opens, the click event bubbles up to $nodes.page which closes the drawer.
        if (evt && evt.stopPropagation) {
          evt.stopPropagation();
          // save the source of the click, we'll focus to this on close
          evt.currentTarget.setAttribute('aria-expanded', 'true');
          this.activeSource = evt.currentTarget;
        } else if (returnFocusEl) {
          returnFocusEl.setAttribute('aria-expanded', 'true');
          this.activeSource = returnFocusEl;
        }

        theme.utils.prepareTransition(this.drawer, function () {
          this.drawer.classList.add(this.config.activeDrawer);
        }.bind(this));

        document.documentElement.classList.add(this.config.openClass);
        this.isOpen = true;

        theme.a11y.trapFocus({
          container: this.drawer,
          namespace: 'drawer_focus'
        });

        document.dispatchEvent(new CustomEvent('drawerOpen'));
        document.dispatchEvent(new CustomEvent('drawerOpen.' + this.config.id));

        this.bindEvents();
      },

      close: function (evt) {
        if (!this.isOpen) {
          return;
        }

        // Do not close if click event came from inside drawer
        if (evt) {
          if (evt.target.closest('.js-drawer-close')) {
            // Do not close if using the drawer close button
          } else if (evt.target.closest('.drawer')) {
            return;
          }
        }

        // deselect any focused form elements
        document.activeElement.blur();

        theme.utils.prepareTransition(this.drawer, function () {
          this.drawer.classList.remove(this.config.activeDrawer);
        }.bind(this));

        document.documentElement.classList.remove(this.config.openClass);
        document.documentElement.classList.add(this.config.closingClass);

        window.setTimeout(function () {
          document.documentElement.classList.remove(this.config.closingClass);
          if (this.activeSource && this.activeSource.getAttribute('aria-expanded')) {
            this.activeSource.setAttribute('aria-expanded', 'false');
            this.activeSource.focus();
          }
        }.bind(this), 500);

        this.isOpen = false;

        theme.a11y.removeTrapFocus({
          container: this.drawer,
          namespace: 'drawer_focus'
        });

        this.unbindEvents();
      },

      bindEvents: function () {
        // Clicking out of drawer closes it
        window.on('click' + this.config.namespace, function (evt) {
          this.close(evt)
          return;
        }.bind(this));

        // Pressing escape closes drawer
        window.on('keyup' + this.config.namespace, function (evt) {
          if (evt.keyCode === 27) {
            this.close();
          }
        }.bind(this));

        theme.a11y.lockMobileScrolling(this.config.namespace, this.nodes.page);
      },

      unbindEvents: function () {
        window.off('click' + this.config.namespace);
        window.off('keyup' + this.config.namespace);

        theme.a11y.unlockMobileScrolling(this.config.namespace, this.nodes.page);
      }
    });

    return Drawers;
  })();

  theme.AjaxProduct = (function () {
    var status = {
      loading: false
    };

    function ProductForm(form, submit, args) {
      this.form = form;
      this.args = args;

      var submitSelector = submit ? submit : '.add-to-cart';

      if (this.form) {
        this.addToCart = form.querySelector(submitSelector);
        this.form.addEventListener('submit', this.addItemFromForm.bind(this));
      }
    };

    ProductForm.prototype = Object.assign({}, ProductForm.prototype, {
      addItemFromForm: function (evt, callback) {
        evt.preventDefault();

        if (status.loading) {
          return;
        }
        // Klaviyo tracking 
        // Loading indicator on add to cart button
        this.addToCart.classList.add('btn--loading');

        status.loading = true;

        var data = theme.utils.serialize(this.form);

        fetch(theme.routes.cartAdd, {
          method: 'POST',
          // TEST: body: JSON.stringify({ "items": [{ "id": 43263929057521, "quantity": 1, "selling_plan": 3062268145 }] }),
          body: data,
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
          .then(response => response.json())
          .then(function (data) {
            if (data.status === 422) {
              this.error(data);
            } else {
              var product = data;
              // Trigger success immediately for better UX
              this.success(product);
              // Apply discount in the background
              theme.cart.applyDiscountFromCookie();
            }

            status.loading = false;
            this.addToCart.classList.remove('btn--loading');

            // Trigger cart build instead of reloading the page
            if (document.body.classList.contains('template-cart')) {
              document.dispatchEvent(new CustomEvent('cart:build'));
            }
          }.bind(this));
      },

      success: function (product) {
        var errors = this.form.querySelector('.errors');
        if (errors) {
          errors.remove();
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: product,
            addToCartBtn: this.addToCart
          }
        }));

        if (this.args && this.args.scopedEventId) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added:' + this.args.scopedEventId, {
            detail: {
              product: product,
              addToCartBtn: this.addToCart
            }
          }));
        }
      },

      error: function (error) {
        if (!error.description) {
          console.warn(error);
          return;
        }

        var errors = this.form.querySelector('.errors');
        if (errors) {
          errors.remove();
        }

        var errorDiv = document.createElement('div');
        errorDiv.classList.add('errors', 'text-center');
        errorDiv.textContent = error.description;
        this.form.append(errorDiv);

        document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
          detail: {
            errorMessage: error.description
          }
        }));

        if (this.args && this.args.scopedEventId) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:error:' + this.args.scopedEventId, {
            detail: {
              errorMessage: error.description
            }
          }));
        }
      }
    });

    return ProductForm;
  })();

  theme.CartDrawer = (function () {
    var selectors = {
      drawer: '#CartDrawer',
      form: '#CartDrawerForm',
      openBtn: '.js-drawer-open-cart'
    };

    function CartDrawer() {
      this.form = document.querySelector(selectors.form);
      this.drawer = new theme.Drawers('CartDrawer', 'cart');

      // Store instance for access elsewhere
      theme.CartDrawer.instance = this;

      this.init();
    }

    CartDrawer.prototype = Object.assign({}, CartDrawer.prototype, {
      init: function () {
        this.cartForm = new theme.CartForm(this.form);

        // On page load, only fetch JSON to update bubble count, skip heavy markup
        this.cartForm.buildCart(null, true);

        // Check if we should open cart after reload
        if (sessionStorage.getItem('openCartAfterReload') === 'true') {
          sessionStorage.removeItem('openCartAfterReload');
          var self = this;
          setTimeout(function () {
            self.open();
          }, 500);
        }

        document.addEventListener('ajaxProduct:added', function (evt) {
          var self = this;

          if (this.cartForm.form) this.cartForm.form.classList.add('cart--busy');

          this.cartForm.buildCart();
          self.open();
        }.bind(this));

        // Dev-friendly way to open cart
        document.addEventListener('cart:open', this.open.bind(this));
        document.addEventListener('cart:close', this.close.bind(this));

        // Listen for drawer open to fetch markup if not already loaded
        document.addEventListener('drawerOpen.CartDrawer', function () {
          if (!this.cartForm.lastCartCount || this.cartForm.lastCartCount > 0) {
            this.cartForm.buildCart();
          }
        }.bind(this));

        // Performance Optimization: Pre-fetch markup on hover of any cart button
        var self = this;
        var prefetchDone = false;
        var prefetch = function () {
          if (!prefetchDone) {
            self.cartForm.buildCart();
            prefetchDone = true;
          }
        };

        document.querySelectorAll(selectors.openBtn).forEach(function (btn) {
          btn.addEventListener('mouseenter', prefetch, { once: true });
          btn.addEventListener('touchstart', prefetch, { once: true });
        });
      },

      open: function () {
        this.drawer.open();
      },

      close: function () {
        this.drawer.close();
      }
    });

    return CartDrawer;
  })();

  window.onpageshow = function (evt) {
    // Removes unload class when returning to page via history
    if (evt.persisted) {
      document.body.classList.remove('unloading');
      document.querySelectorAll('.cart__checkout').forEach(el => {
        el.classList.remove('btn--loading');
      });
    }
  };

  theme.HeaderSection = (function () {
    function HeaderSection(container) {
      this.container = container;
      this.sectionId = this.container.getAttribute('data-section-id');

      this.init();
    }

    HeaderSection.prototype = Object.assign({}, HeaderSection.prototype, {
      init: function () {
        this.initDrawers();
      },

      initDrawers: function () {
        theme.NavDrawer = new theme.Drawers('NavDrawer', 'nav');
        if (theme.settings.cartType === 'drawer') {
          new theme.CartDrawer();
        }
      },

      onUnload: function () {
        theme.NavDrawer.close();
      }
    });

    return HeaderSection;
  })();

  theme.Product = (function () {

    function Product(container) {
      this.container = container;
      var sectionId = this.sectionId = container.getAttribute('data-section-id');

      this.settings = {
        enableHistoryState: container.dataset.history || false,
        namespace: '.product-' + sectionId,
        inventory: false,
        inventoryThreshold: 10
      };

      this.selectors = {
        variantsJson: '[data-variant-json]',
        currentVariantJson: '[data-current-variant-json]',
        subscriptionJson: '[data-subscription-json]',
        form: '.product-single__form',

        price: '[data-product-price]',
        priceSubscription: '[data-product-price-subscription]',
        oldPrice: '[data-product-old-price]',
        totalPrice: '[data-product-price-total]',
        discountedPrice: '[data-product-price-discounted]',
        discount: '[data-product-discount]',

        purchaseType: '[name="purchase_type"]',

        addToCart: '[data-add-to-cart]',
        addToCartText: '[data-add-to-cart-text]',

        originalSelectorId: '[data-product-select]',
        singleOptionSelector: '[data-variant-input]',

        dropdown: '.variant-dropdown-container',
        variantDropdown: '.variant-dropdown',
        variantDropdownItems: '.variant-dropdown-item',

        sellingPlan: '[name="selling_plan"]',
      };

      this.cacheElements();

      this.init();
    }

    Product.prototype = Object.assign({}, Product.prototype, {
      init: function () {
        this.formSetup();
      },

      cacheElements: function () {
        this.cache = {
          form: this.container.querySelector(this.selectors.form),

          price: this.container.querySelectorAll(this.selectors.price),
          priceSubscription: this.container.querySelectorAll(this.selectors.priceSubscription),
          oldPrice: this.container.querySelectorAll(this.selectors.oldPrice),
          totalPrice: this.container.querySelectorAll(this.selectors.totalPrice),
          discountedPrice: this.container.querySelectorAll(this.selectors.discountedPrice),
          discount: this.container.querySelectorAll(this.selectors.discount),
        };
      },

      formSetup: function () {
        this.initAjaxProductForm();
        this.initVariants();
        this.initSubscription();
      },

      initAjaxProductForm: function () {
        new theme.AjaxProduct(this.cache.form);
      },

      initVariants: function () {
        var variantJson = this.container.querySelector(this.selectors.variantsJson);

        if (!variantJson) {
          return;
        }

        this.variantsObject = JSON.parse(variantJson.innerHTML);

        var options = {
          container: this.container,
          enableHistoryState: this.settings.enableHistoryState,
          singleOptionSelector: this.selectors.singleOptionSelector,
          originalSelectorId: this.selectors.originalSelectorId,
          variants: this.variantsObject,

          dropdown: this.selectors.dropdown,
          variantDropdown: this.selectors.variantDropdown,
          variantDropdownItems: this.selectors.variantDropdownItems,

          sellingPlan: this.selectors.sellingPlan
        };

        this.variants = new theme.Variants(options);

        // Product availability on page load
        if (this.storeAvailability) {
          var variant_id = this.variants.currentVariant ? this.variants.currentVariant.id : this.variants.variants[0].id;

          this.storeAvailability.updateContent(variant_id);
          this.container.on('variantChange' + this.settings.namespace, this.updateAvailability.bind(this));
        }

        this.container.on('variantChange' + this.settings.namespace, this.updateCartButton.bind(this));
        // this.container.on('variantImageChange' + this.settings.namespace, this.updateVariantImage.bind(this));
        this.container.on('variantPriceChange' + this.settings.namespace, this.updatePrice.bind(this));
        this.container.on('variantQuantityChange' + this.settings.namespace, this.updateQuantity.bind(this));
        this.container.on('variantUnitPriceChange' + this.settings.namespace, this.updateUnitPrice.bind(this));

        if (this.container.querySelector(this.selectors.sku)) {
          this.container.on('variantSKUChange' + this.settings.namespace, this.updateSku.bind(this));
        }

        var inventoryEl = this.container.querySelector(this.selectors.inventory);
        if (inventoryEl) {
          this.settings.inventory = true;
          this.settings.inventoryThreshold = inventoryEl.dataset.threshold;
          this.container.on('variantChange' + this.settings.namespace, this.updateInventory.bind(this));
        }


        // Update individual variant availability on each selection
        if (theme.settings.dynamicVariantsEnable) {
          var currentVariantJson = this.container.querySelector(this.selectors.currentVariantJson);

          if (currentVariantJson) {
            var variantType = this.container.querySelector(this.selectors.variantType);

            if (variantType) {
              new theme.VariantAvailability({
                container: this.container,
                namespace: this.settings.namespace,
                type: variantType.dataset.type,
                variantsObject: this.variantsObject,
                currentVariantObject: JSON.parse(currentVariantJson.innerHTML)
              });
            }
          }
        }

        // image set names variant change listeners
        if (this.settings.imageSetName) {
          var variantWrapper = this.container.querySelector('.variant-input-wrap[data-handle="' + this.settings.imageSetName + '"]');
          if (variantWrapper) {
            this.settings.imageSetIndex = variantWrapper.dataset.index;
            this.container.on('variantChange' + this.settings.namespace, this.updateImageSet.bind(this))
          } else {
            this.settings.imageSetName = null;
          }
        }
      },

      initSubscription: function () {
        var subscriptionJson = this.container.querySelector(this.selectors.subscriptionJson);

        if (!subscriptionJson) {
          return;
        }

        this.subscriptionObject = JSON.parse(subscriptionJson.innerHTML);

        var options = {
          container: this.container,
          purchaseTypeSelector: this.selectors.purchaseType,
          originalSelectorId: this.selectors.originalSelectorId,
          subscription: this.subscriptionObject
        };

        this.Subscription = new theme.Subscription(options);

        this.container.on('subscriptionPriceChange' + this.settings.namespace, this.updatePrice.bind(this));

      },

      /*============================================================================
        Variant change methods
      ==============================================================================*/
      updateCartButton: function (evt) {
        var variant = evt.detail.variant;
        var cartBtn = this.container.querySelector(this.selectors.addToCart);
        var cartBtnText = this.container.querySelector(this.selectors.addToCartText);

        if (variant) {
          if (variant.available) {
            // Available, enable the submit button and change text
            // cartBtn.classList.remove(classes.disabled);
            cartBtn.disabled = false;
            var defaultText = cartBtnText.dataset.defaultText;
            cartBtnText.textContent = defaultText;
          } else {
            // Sold out, disable the submit button and change text
            cartBtn.classList.add(classes.disabled);
            cartBtn.disabled = true;
            cartBtnText.textContent = theme.strings.soldOut;
          }
        } else {
          // The variant doesn't exist, disable submit button
          cartBtn.classList.add(classes.disabled);
          cartBtn.disabled = true;
          cartBtnText.textContent = theme.strings.unavailable;
        }
      },

      updatePrice: function (evt) {
        var variant = this.variantsObject.filter(function (variant) { return evt.detail.variant === variant.id; })[0];
        var totalPrice = variant.price;

        if (variant) {
          var discount = 0;

          if (this.subscriptionObject && this.subscriptionObject.discount_percentage) {
            discount = parseInt(this.subscriptionObject.discount_percentage) / 100;
          }

          var purchaseTypeElement = this.container.querySelector(this.selectors.purchaseType + ':checked');

          // Check if the purchaseTypeElement exists and has the value property
          var purchaseType = purchaseTypeElement && purchaseTypeElement.value ? purchaseTypeElement.value : '';

          // Total price
          Array.prototype.forEach.call(this.cache.totalPrice, function (price) {
            totalPrice = purchaseType === 'onetime' ? variant.price : variant.price - variant.price * discount;
            price.innerHTML = theme.Currency.formatMoney(totalPrice, theme.settings.moneyFormat);
          });

          //Discounted price
          Array.prototype.forEach.call(this.cache.discountedPrice, function (price) {
            var discountedTotal = totalPrice * 0.5;
            price.innerHTML = theme.Currency.formatMoney(discountedTotal, theme.settings.moneyFormat);
          });

          // Original price
          var priceOld = parseInt(variant.option1) === 14 || parseInt(variant.option1) === 28 ? variant.price * parseInt(variant.option1) : variant.price;
          Array.prototype.forEach.call(this.cache.oldPrice, function (price) {
            price.innerHTML = theme.Currency.formatMoney(priceOld, theme.settings.moneyFormat);
            price.classList.toggle('d-none', priceOld === totalPrice);
          });

          // Regular price
          Array.prototype.forEach.call(this.cache.price, function (price) {
            price.innerHTML = theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat);
          });

          // Subscription price
          Array.prototype.forEach.call(this.cache.priceSubscription, function (price) {
            var subscriptionPrice = variant.price - variant.price * discount;
            price.innerHTML = theme.Currency.formatMoney(subscriptionPrice, theme.settings.moneyFormat);
          });

          // Discount
          Array.prototype.forEach.call(this.cache.discount, function (price) {
            var discountVal = variant.price * discount;
            price.innerHTML = theme.Currency.formatMoney(discountVal, theme.settings.moneyFormat)
          });
        }
      },

      updateQuantity: function (evt) {
      },

      updateUnitPrice: function (evt) {
        var variant = evt.detail.variant;

        if (variant && variant.unit_price) {
          this.container.querySelector(this.selectors.unitPrice).innerHTML = theme.Currency.formatMoney(variant.unit_price, theme.settings.moneyFormat);
          this.container.querySelector(this.selectors.unitPriceBaseUnit).innerHTML = theme.Currency.getBaseUnit(variant);
          this.container.querySelector(this.selectors.unitWrapper).classList.remove(classes.hidden);
        } else {
          this.container.querySelector(this.selectors.unitWrapper).classList.add(classes.hidden);
        }
      },

      updateVariantImage: function (evt) {
        var variant = evt.detail.variant;
        var sizedImgUrl = theme.Images.getSizedImageUrl(variant.featured_media.preview_image.src, this.settings.imageSize);

        var newImage = this.container.querySelector('.product__thumb[data-id="' + variant.featured_media.id + '"]');
        var imageIndex = this.getThumbIndex(newImage);

        // If there is no index, slider is not initalized
        if (typeof imageIndex === 'undefined') {
          return;
        }

        // Go to that variant image's slide
        if (this.flickity) {
          this.flickity.goToSlide(imageIndex);
        }
      },

      updateSku: function (evt) {
        var variant = evt.detail.variant;
        var newSku = '';

        if (variant) {
          if (variant.sku) {
            newSku = variant.sku;
          }

          this.container.querySelector(this.selectors.sku).textContent = newSku;
        }
      }
    });

    return Product;
  })();

  theme.ShopNow = (function () {
    var selectors = {
      checkoutBtn: '.checkout-btn'
    };

    function ShopNow() {
      this.checkoutBtn = document.querySelector(selectors.checkoutBtn);
    }

    ShopNow.prototype = Object.assign({}, ShopNow.prototype, {
      init: function () {
        if (this.checkoutBtn) {
          this.checkoutBtn.on('click', this.onClick.bind(this));
        }
      },

      onClick: function (evt) {
        evt.preventDefault();
      }
    });

    return ShopNow;
  })();

  theme.isStorageSupported = function (type) {
    // Return false if we are in an iframe without access to sessionStorage
    if (window.self !== window.top) {
      return false;
    }

    var testKey = 'test';
    var storage;
    if (type === 'session') {
      storage = window.sessionStorage;
    }
    if (type === 'local') {
      storage = window.localStorage;
    }

    try {
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  };
  /*============================================================================
    Things that don't require DOM to be ready
  ==============================================================================*/
  theme.config.hasSessionStorage = theme.isStorageSupported('session');
  theme.config.hasLocalStorage = theme.isStorageSupported('local');

  /*============================================================================
    Cart drawer header benefits
  ==============================================================================*/
  theme.syncCartDrawerExtras = function () {
    function moveIntoSlot(slotId, sourceSelector) {
      var slot = document.getElementById(slotId);
      if (!slot) return;
      var source = document.querySelector(sourceSelector);
      slot.innerHTML = '';
      if (source) slot.appendChild(source);
    }

    moveIntoSlot('drawer-benefits-slot', '[data-products] .drawer__purchase-benefits');
    moveIntoSlot('drawer-extras-slot', '[data-products] .fresh-bonus-items-list');
  };

  document.addEventListener('cart:updated', theme.syncCartDrawerExtras);

  /*============================================================================
    Things that require DOM to be ready
  ==============================================================================*/
  function DOMready(callback) {
    if (document.readyState != 'loading') callback();
    else document.addEventListener('DOMContentLoaded', callback);
  }

  // Load generic JS. Also reinitializes when sections are
  // added, edited, or removed in Shopify's editor
  theme.initGlobals = function () {
    theme.collapsibles.init();
  }

  DOMready(function () {
    theme.sections = new theme.Sections();

    theme.sections.register('header', theme.HeaderSection);
    theme.sections.register('product', theme.Product);

    if (document.body.classList.contains('template-cart')) {
      var cartPageForm = document.getElementById('CartPageForm');
      if (cartPageForm) {
        new theme.CartForm(cartPageForm);
      }
    }

    // new theme.ShopNow();

    document.dispatchEvent(new CustomEvent('page:loaded'));
  });

})();

/*============================================================================
  Product page modals (recipe / guarantee / vet)
  Shared open/close/overlay/Escape behaviour for any element using the
  data-<name>-modal / data-<name>-modal-open / data-<name>-modal-close hooks.
  The recipe modal additionally syncs sachets ("28x 550g") and duration
  ("28 Tage") from the selected variant barcode (CG-028-0550-...).
  Markup: snippets/standard-modern-(recipe|guarantee|vet)-modal.liquid
==============================================================================*/
(function () {
  if (window.productPageModalsBound) return;
  window.productPageModalsBound = true;

  var BODY_CLASS = 'product-page-modal-open';

  // Registered modals. `onOpen` is an optional hook run before opening.
  var modals = [
    { name: 'recipe', onOpen: updateRecipeSachets },
    { name: 'guarantee' },
    { name: 'vet' }
  ];

  function getModal(name) {
    return document.querySelector('[data-' + name + '-modal]');
  }

  function openModal(entry) {
    var modal = getModal(entry.name);
    if (!modal) return;
    if (typeof entry.onOpen === 'function') entry.onOpen();
    modal.classList.add('is-open');
    document.body.classList.add(BODY_CLASS);
  }

  function closeModal(name) {
    var modal = getModal(name);
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove(BODY_CLASS);
  }

  function closeAllModals() {
    modals.forEach(function (entry) { closeModal(entry.name); });
  }

  document.addEventListener('click', function (e) {
    for (var i = 0; i < modals.length; i++) {
      var entry = modals[i];
      if (e.target.closest('[data-' + entry.name + '-modal-open]')) {
        e.preventDefault();
        openModal(entry);
        return;
      }
      if (e.target.closest('[data-' + entry.name + '-modal-close]')) {
        e.preventDefault();
        closeModal(entry.name);
        return;
      }
    }
    // Click on the overlay itself (outside the dialog) closes that modal.
    for (var j = 0; j < modals.length; j++) {
      var modal = getModal(modals[j].name);
      if (modal && modal.classList.contains('is-open') && e.target === modal) {
        closeModal(modals[j].name);
        return;
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllModals();
  });

  /* ----- Recipe modal: sachets + duration from the variant barcode -------- */
  function updateRecipeSachets() {
    var form = document.querySelector('#add-to-cart-form');
    var mapEl = document.querySelector('[data-recipe-variant-barcodes]');
    if (!form || !mapEl) return;

    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return;

    var map = {};
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }

    // Per-recipe days come from metafields (barcode pouch count != days), keyed by variant.
    var daysMapEl = document.querySelector('[data-recipe-variant-days]');
    var daysMap = {};
    if (daysMapEl) { try { daysMap = JSON.parse(daysMapEl.textContent); } catch (e) { daysMap = {}; } }
    var variantDays = daysMap[idInput.value] || {};

    var barcode = String(map[idInput.value] || '');

    // Group barcode triplets per recipe. The new barcode structure can list a
    // recipe twice with two different pouch sizes (e.g. GS-008-800-GS-008-600),
    // so we collect every non-empty slot per recipe instead of just the first.
    var slotsByCode = { CG: [], GS: [], RT: [], WS: [] };
    var tripletRe = /([A-Za-z]{2})-(\d+)-(\d+)/g;
    var m;
    while ((m = tripletRe.exec(barcode)) !== null) {
      var recipe = m[1].toUpperCase();
      if (!slotsByCode[recipe]) continue;
      var slotAmount = parseInt(m[2], 10) || 0;
      var slotGram = parseInt(m[3], 10) || 0;
      if (slotAmount > 0) slotsByCode[recipe].push({ amount: slotAmount, gram: slotGram });
    }

    ['CG', 'GS', 'RT', 'WS'].forEach(function (code) {
      var slots = slotsByCode[code];
      if (!slots.length) return;

      // Sachets: list every pouch size, e.g. "8 × 800 g, 8 × 600 g" — the same format
      // snippets/fresh-recipe-rows.liquid renders.
      var sachetText = slots
        .map(function (s) { return s.amount + ' × ' + s.gram + ' g'; })
        .join(', ');

      // EVERY copy of the row, not just the first: snippets/fresh-recipe-rows.liquid
      // renders the same rows in the recipe modal and in the "Dein Paket" card on the
      // product form. querySelector() would leave the modal stale, because the
      // cards come first in the DOM.
      var rows = document.querySelectorAll('[data-recipe="' + code + '"]');
      Array.prototype.forEach.call(rows, function (row) {
        var sachetEl = row.querySelector('[data-recipe-sachets]');
        var durationEl = row.querySelector('[data-recipe-duration]');

        if (sachetEl) sachetEl.textContent = sachetText;

        // Duration is a per-recipe value from metafields (the barcode pouch count
        // is not the number of days, e.g. 20 pouches can last 10 days). The suffix
        // is the Theme Editor wording the Liquid printed, not a hardcoded "Tage".
        if (durationEl) {
          durationEl.textContent =
            (variantDays[code] || 0) + ' ' + (durationEl.getAttribute('data-days-suffix') || 'Tage');
        }
      });
    });
  }

  // Keep sachets/duration in sync as the user changes recipe / pension / cycle.
  function onFormInteraction(e) {
    var t = e.target;
    if (!t) return;
    if (
      t.name === 'pension' ||
      t.name === 'selling_plan' ||
      t.name === 'purchase_type' ||
      (t.closest && t.closest('#add-to-cart-form'))
    ) {
      // let fresh-product-form.js update [name="id"] first
      setTimeout(updateRecipeSachets, 0);
    }
  }

  document.addEventListener('change', onFormInteraction);
  document.addEventListener('input', onFormInteraction);

  // Populate once on load so the modal is correct before any interaction
  // (defer so fresh-product-form.js can set [name="id"] first).
  setTimeout(updateRecipeSachets, 0);
})();
